import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';

// UI Library imports
import { Button } from '../ui/form';
import { Text } from '../ui/common';

const Container = styled.div`
  padding: 1rem;
`;

const FileUploadArea = styled.div<{ isDragOver: boolean }>`
  border: 2px dashed ${props => props.isDragOver ? '#667eea' : '#e1e8ed'};
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  background: ${props => props.isDragOver ? '#f8f9ff' : '#fafbfc'};
  transition: all 0.2s ease;
  cursor: pointer;
  margin-bottom: 1rem;

  &:hover {
    border-color: #667eea;
    background: #f8f9ff;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadText = styled(Text)`
  color: #64748b;
  margin-bottom: 0.5rem;
`;

const FileInfo = styled.div`
  background: #f1f5f9;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  border-left: 4px solid #667eea;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e1e8ed;
  border-radius: 4px;
  overflow: hidden;
  margin: 1rem 0;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const ResultsContainer = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
`;

const ResultItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
`;

const ErrorList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 6px;
  padding: 1rem;
  margin-top: 1rem;
`;

const ErrorItem = styled.div`
  color: #c53030;
  font-size: 0.875rem;
  padding: 0.25rem 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

interface BulkDataUpdateProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface UploadResult {
  totalRows: number;
  updatedCount: number;
  skippedCount: number;
  errors?: string[];
}

const BulkDataUpdate: React.FC<BulkDataUpdateProps> = ({ onSuccess, onCancel }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xls|xlsx)$/i)) {
      setError('Si prega di selezionare un file Excel valido (.xls o .xlsx)');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('La dimensione del file deve essere inferiore a 10MB');
      return;
    }

    setSelectedFile(file);
    setError(null);
    setUploadResult(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post('/api/contacts/bulk-update', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      });

      if (response.data.success) {
        setUploadResult(response.data.data);
        // Auto-close modal after successful upload
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setError(response.data.message || 'Caricamento fallito');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Impossibile caricare il file');
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Container>
      <Text style={{ marginBottom: '1rem', color: '#64748b' }}>
        Carica un file Excel (.xls o .xlsx) con le colonne: <strong>linkedin_url</strong>, <strong>phone</strong>, e <strong>email</strong>.
        Il sistema aggiornerà i contatti esistenti con URL LinkedIn corrispondenti.
      </Text>

      {!selectedFile && !uploadResult && (
        <FileUploadArea
          isDragOver={isDragOver}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadText>
            Trascina qui il tuo file Excel o clicca per sfogliare
          </UploadText>
          <Text style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
            Formati supportati: .xls, .xlsx (max 10MB)
          </Text>
        </FileUploadArea>
      )}

      <FileInput
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx"
        onChange={handleFileInputChange}
      />

      {selectedFile && !uploadResult && (
        <FileInfo>
          <Text><strong>File selezionato:</strong> {selectedFile.name}</Text>
          <Text><strong>Dimensione:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</Text>
          <Text><strong>Tipo:</strong> {selectedFile.type || 'File Excel'}</Text>
        </FileInfo>
      )}

      {isUploading && (
        <>
          <ProgressBar>
            <ProgressFill progress={uploadProgress} />
          </ProgressBar>
          <Text style={{ textAlign: 'center', color: '#64748b' }}>
            Caricamento ed elaborazione in corso... {uploadProgress}%
          </Text>
        </>
      )}

      {uploadResult && (
        <ResultsContainer>
          <Text style={{ fontWeight: 'bold', marginBottom: '1rem', color: '#059669' }}>
            Caricamento completato con successo!
          </Text>
          <ResultItem>
            <Text>Righe totali elaborate:</Text>
            <Text><strong>{uploadResult.totalRows}</strong></Text>
          </ResultItem>
          <ResultItem>
            <Text>Contatti aggiornati:</Text>
            <Text style={{ color: '#059669' }}><strong>{uploadResult.updatedCount}</strong></Text>
          </ResultItem>
          <ResultItem>
            <Text>Righe saltate:</Text>
            <Text style={{ color: '#d97706' }}><strong>{uploadResult.skippedCount}</strong></Text>
          </ResultItem>

          {uploadResult.errors && uploadResult.errors.length > 0 && (
            <ErrorList>
              <Text style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#c53030' }}>
                Errori riscontrati:
              </Text>
              {uploadResult.errors.map((error, index) => (
                <ErrorItem key={index}>{error}</ErrorItem>
              ))}
            </ErrorList>
          )}
        </ResultsContainer>
      )}

      {error && (
        <div style={{ 
          background: '#fff5f5', 
          border: '1px solid #fed7d7', 
          borderRadius: '6px', 
          padding: '1rem', 
          margin: '1rem 0' 
        }}>
          <Text style={{ color: '#c53030' }}>{error}</Text>
        </div>
      )}

      <ButtonContainer>
        <Button variant="secondary" onClick={onCancel}>
          Annulla
        </Button>
        {selectedFile && !uploadResult && (
          <Button onClick={resetForm} style={{ marginRight: '0.5rem' }}>
            Pulisci
          </Button>
        )}
        {selectedFile && !isUploading && !uploadResult && (
          <Button variant="primary" onClick={handleUpload}>
            Carica ed Elabora
          </Button>
        )}
        {uploadResult && (
          <Button variant="primary" onClick={onSuccess}>
            Fatto
          </Button>
        )}
      </ButtonContainer>
    </Container>
  );
};

export default BulkDataUpdate;